import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import api from '../../../Services/api';
import { DatasetFormMode } from '../DatasetForm';

const DataMappingEditor = ({ 
    datasetVariables = [],
    initialMappings = [],
    onChange = () => {} ,
    mode = DatasetFormMode.NEW
}) => {
  const [mappingTypes, setMappingTypes] = useState([]);
  const [mappings, setMappings] = useState([]);

  const updateMappings = (newMappings) => {
    setMappings(newMappings);
    onChange(newMappings);
  };

  const areMappingsEqual = (m1, m2) => {
    if (m1.length !== m2.length) return false;
    for (let i = 0; i < m1.length; i++) {
      if (m1[i].type !== m2[i].type || m1[i].variableName !== m2[i].variableName) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const fetchMappingTypes = async () => {
      try {
        const response = await api.get('/api/datasets/mapping-types');
        setMappingTypes(response.data);
      } catch (error) {
        console.error('Error fetching mapping types:', error);
      }
    };
    fetchMappingTypes();
  }, []);

  useEffect(() => {
    if (mappingTypes.length > 0 && mode === DatasetFormMode.NEW) {
      if (mappingTypes.length > 0 && mappings.length === 0) {
        mapVariablesDynamically(mappingTypes);
      }
    } else {
      if (!areMappingsEqual(mappings, initialMappings)) {
        updateMappings(initialMappings);
      }
    }
  }, [mappingTypes, datasetVariables, mode, initialMappings]);

  const mapVariablesDynamically = (fetchedMappingTypes) => {
    const dynamicMappings = fetchedMappingTypes.reduce((acc, mappingType) => {
      if (mappingType.variableName && datasetVariables.includes(mappingType.variableName)) {
        acc.push({ type: mappingType.type, variableName: mappingType.variableName });
      }
      return acc;
    }, []);
    
    if (!areMappingsEqual(mappings, dynamicMappings)) {
      updateMappings(dynamicMappings);
    }
  };

  const handleAddMapping = () => {
    if (!mappingTypes.length || !datasetVariables.length) return;
    const newMapping = { 
      type: mappingTypes[0].type, 
      variableName: '' 
    };
    updateMappings([...mappings, newMapping]);
  };

  const handleRemoveMapping = (index) => {
    updateMappings(mappings.filter((_, i) => i !== index));
  };

  const handleMappingTypeChange = (index, newTypeId) => {
    const updated = mappings.map((mapping, i) => 
      i === index ? { ...mapping, type: parseInt(newTypeId, 10) } : mapping
    );
    updateMappings(updated);
  };

  const handleVariableNameChange = (index, newVariableName) => {
    const updated = mappings.map((mapping, i) => 
      i === index ? { ...mapping, variableName: newVariableName } : mapping
    );
    updateMappings(updated);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddMapping}
          disabled={!mappingTypes.length || !datasetVariables.length}
        >
          Add Mapping
        </Button>
      </Box>
      
      {mappings.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
          No mappings defined. Click "Add Mapping" to create one.
        </Typography>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2, fontWeight: 'bold' }}>
            <Grid item xs={5}><Typography>Mapping Type</Typography></Grid>
            <Grid item xs={5}><Typography>Dataset Variable</Typography></Grid>
            <Grid item xs={2}><Typography align="center">Actions</Typography></Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
          {mappings.map((mapping, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <Select
                    value={mapping.type || ''}
                    onChange={(e) => handleMappingTypeChange(index, e.target.value)}
                  >
                    {mappingTypes.map((map) => (
                      <MenuItem key={map.type} value={map.type}>
                        {map.variableName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <Select
                    value={mapping.variableName || ''}
                    onChange={(e) => handleVariableNameChange(index, e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Select a variable</em>
                    </MenuItem>
                    {datasetVariables.map((variable) => (
                      <MenuItem key={variable} value={variable}>{variable}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveMapping(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </>
      )}
    </Box>
  );
};

export default DataMappingEditor;